import { useCallback, useEffect, useState } from 'react';
import { Link, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { Shell } from '../components/Layout.js';
import { api, ApiError } from '../api/client.js';
import { useDocumentMeta } from '../lib/document-meta.js';
import { useT } from '../state/i18n.js';

export function EditorPage(): JSX.Element {
  const t = useT();
  const navigate = useNavigate();
  const { proposalId = '' } = useParams<{ proposalId: string }>();
  const [params] = useSearchParams();
  const ownerKey = params.get('key');

  const [html, setHtml] = useState('');
  const [preview, setPreview] = useState('');
  const [title, setTitle] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [dirty, setDirty] = useState(false);
  const [savedAt, setSavedAt] = useState<Date | null>(null);

  useDocumentMeta({ title: t({ ko: 'HTML 편집', en: 'Edit HTML' }), noindex: true });

  // Load the current source.
  useEffect(() => {
    let alive = true;
    setLoading(true);
    api
      .getProposalSource(proposalId, ownerKey)
      .then((res) => {
        if (!alive) return;
        setHtml(res.html);
        setPreview(res.html);
        setTitle(res.title);
        setError(null);
      })
      .catch((err: ApiError) =>
        setError(err.message || t({ ko: '불러오지 못했습니다.', en: 'Could not load.' })),
      )
      .finally(() => alive && setLoading(false));
    return () => {
      alive = false;
    };
  }, [proposalId, ownerKey, t]);

  // Debounce the live preview so typing stays smooth.
  useEffect(() => {
    const id = window.setTimeout(() => setPreview(html), 250);
    return () => window.clearTimeout(id);
  }, [html]);

  const save = useCallback(async (): Promise<void> => {
    setSaving(true);
    setError(null);
    try {
      const result = await api.updateProposalSource(proposalId, ownerKey, html);
      setDirty(false);
      setSavedAt(new Date());
      if (result.id && result.id !== proposalId) navigate(`/edit/${result.id}${ownerKey ? `?key=${encodeURIComponent(ownerKey)}` : ''}`, { replace: true });
    } catch (err) {
      setError(err instanceof Error ? err.message : t({ ko: '저장 실패', en: 'Save failed' }));
    } finally {
      setSaving(false);
    }
  }, [proposalId, ownerKey, html, t, navigate]);

  // Cmd/Ctrl+S to save.
  useEffect(() => {
    function onKey(e: KeyboardEvent): void {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 's') {
        e.preventDefault();
        if (dirty && !saving) void save();
      }
    }
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [dirty, saving, save]);

  return (
    <Shell>
      <div className="editor">
        <div className="editor-bar">
          <div className="editor-bar__meta">
            <Link to={`/dashboard/${proposalId}${ownerKey ? `?key=${encodeURIComponent(ownerKey)}` : ''}`} className="editor-back">
              ← {t({ ko: '대시보드', en: 'Dashboard' })}
            </Link>
            <span className="editor-title" title={title}>
              {title || t({ ko: 'HTML 편집', en: 'Edit HTML' })}
            </span>
          </div>
          <div className="editor-bar__actions">
            <span className="editor-savestate">
              {saving
                ? t({ ko: '저장 중…', en: 'Saving…' })
                : dirty
                  ? t({ ko: '저장 안 됨', en: 'Unsaved' })
                  : savedAt
                    ? t({ ko: '저장됨', en: 'Saved' })
                    : ''}
            </span>
            <button className="cta-primary" onClick={() => void save()} disabled={saving || !dirty}>
              {t({ ko: '저장', en: 'Save' })}
            </button>
          </div>
        </div>

        {loading ? (
          <p className="muted editor-status">{t({ ko: '불러오는 중…', en: 'Loading…' })}</p>
        ) : (
          <div className="editor-split">
            <div className="editor-pane">
              <div className="editor-pane__label">{t({ ko: 'HTML 코드', en: 'HTML code' })}</div>
              <textarea
                className="editor-code"
                value={html}
                disabled={saving}
                spellCheck={false}
                onChange={(e) => {
                  setHtml(e.target.value);
                  setDirty(true);
                }}
                aria-label={t({ ko: 'HTML 코드', en: 'HTML code' })}
              />
            </div>
            <div className="editor-pane">
              <div className="editor-pane__label">{t({ ko: '미리보기', en: 'Preview' })}</div>
              <iframe
                className="editor-preview"
                title={t({ ko: '미리보기', en: 'Preview' })}
                srcDoc={preview}
                sandbox="allow-same-origin"
              />
            </div>
          </div>
        )}
        {error && <p className="status err editor-status" role="alert">{error}</p>}

        <p className="editor-hint muted small">
          {t({
            ko: '저장하면 같은 공유 링크가 바로 새 버전을 보여줍니다. ⌘/Ctrl + S 로도 저장돼요.',
            en: 'Saving updates the same share link instantly. ⌘/Ctrl + S also saves.',
          })}
        </p>
      </div>
    </Shell>
  );
}
